import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi, InvalidateArr } from '@/modules';
// Imported from the file rather than from `@/modules/billing`: the billing barrel pulls in
// `billing.slice`, which imports this very module's barrel back.
import { deskPaymentStateTags } from '@/modules/billing/deskPayment.slice';
import { SurgeryInfoService } from './surgeryInfo.service';
import { SurgeryInfo } from './surgeryInfo.model';
import { wrapError } from '../../error';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';

export const surgeryInfoApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    surgeryInfo: builder.query<SurgeryInfo, PatientId>({
      queryFn: ({ patientId }) =>
        SurgeryInfoService.getInstance(patientId).fetch().catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'SurgeryInfo', id: patientId }
      ]
    }),

    clearExtractedCcamCodes: builder.mutation<SurgeryInfo, PatientId>({
      queryFn: ({ patientId }) =>
        SurgeryInfoService.getInstance(patientId)
          .clearExtractedCcamCodes()
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        error ? [] : [{ type: 'SurgeryInfo', id: patientId }, 'Dashboard']
    }),

    removeExtractedCcamCode: builder.mutation<
      SurgeryInfo,
      PatientId & { code: string }
    >({
      queryFn: ({ patientId, code }) =>
        SurgeryInfoService.getInstance(patientId)
          .removeExtractedCcamCode(code)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        error ? [] : [{ type: 'SurgeryInfo', id: patientId }, 'Dashboard']
    }),

    removeSurgeryAutomationCcamCode: builder.mutation<
      SurgeryInfo,
      PatientId & { code: string }
    >({
      queryFn: ({ patientId, code }) =>
        SurgeryInfoService.getInstance(patientId)
          .removeSurgeryAutomationCcamCode(code)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        error ? [] : [{ type: 'SurgeryInfo', id: patientId }, 'Dashboard']
    }),

    patchSurgeryInfo: builder.mutation<
      SurgeryInfo,
      WithPatientId<Partial<SurgeryInfo>>
    >({
      queryFn: ({ patientId, ...surgeryInfo }) =>
        SurgeryInfoService.getInstance(patientId)
          .patch(surgeryInfo)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, ...payload }) => {
        if (error) {
          return [];
        }

        const arr: InvalidateArr = [
          'SurgeonName',
          { type: 'SurgeryInfo', id: patientId }
        ];

        const keysToUpdate = Object.keys(payload ?? {});
        if (
          keysToUpdate.length === 1 &&
          keysToUpdate[0] === 'automaticAsaEnabled'
        ) {
          return arr;
        }

        arr.push({ type: 'Terra', id: patientId }, 'Dashboard');

        if (payload.asaScore !== undefined) {
          arr.push({
            type: 'ActiveSurgicalEvent',
            id: patientId
          });

          if (payload.emergency !== undefined) {
            arr.push({
              type: 'Scores',
              id: patientId
            });
            arr.push({ type: 'MedicalHo', id: patientId });
          }
        }

        if (
          payload.careType !== undefined ||
          payload.emergency !== undefined ||
          payload.surgeries !== undefined ||
          payload.combinedOrderedSurgeries !== undefined ||
          payload.userDefinedSurgeries !== undefined
        ) {
          arr.push({ type: 'Meds', id: patientId });
        }

        if (
          payload.surgeries !== undefined ||
          payload.combinedOrderedSurgeries !== undefined
        ) {
          arr.push({ type: 'ActiveSurgicalEvent', id: patientId });
          arr.push({ type: 'PregnancyInfo', id: patientId });
          arr.push({ type: 'AnesthesiaInfo', id: patientId });
          arr.push({ type: 'AnesthesiaRisk', id: patientId });
          arr.push({ type: 'LabTest', id: patientId });
          [
            ProtocolName.CARDIAC_STRESS_TEST,
            ProtocolName.PREOP_LAB_TEST,
            ProtocolName.GENERIC_PRESCRIPTION
          ].forEach((protocol) => {
            arr.push({
              type: 'Protocol',
              id: constructProtocolCacheKey({
                patientId,
                protocol
              })
            });
          });
        }

        if (payload.surgeryDate !== undefined) {
          arr.push({ type: 'Scores', id: patientId });
          // A card hold's renewal fields are derived from the surgery date server-side:
          // postponing the surgery past the hold's window is what makes renewals necessary,
          // moving it earlier is what removes the need, and either way the payment has to be
          // re-read rather than left promising the old plan.
          arr.push(...deskPaymentStateTags());
        }

        return arr;
      }
    })
  }),
  overrideExisting: false
});

export const {
  useSurgeryInfoQuery,
  usePatchSurgeryInfoMutation,
  useClearExtractedCcamCodesMutation,
  useRemoveExtractedCcamCodeMutation,
  useRemoveSurgeryAutomationCcamCodeMutation
} = surgeryInfoApi;
